// Subsistema de sonido de alarmas (Web Audio API, sin archivos).
// Reglas (spec sección 11):
//  - ALTA: patrón repetitivo hasta que alguien TOMA la alarma. Mientras haya >=1 ALTA sin tomar,
//    suena; tomar una NO silencia las otras (el motor se basa en el conjunto de alarmas sin tomar).
//  - MEDIA: chime corto al llegar + repite cada chime_media_repite_seg (SLA del sitio, snapshot).
//  - BAJA: sin sonido.
//  - El navegador bloquea autoplay: hay que habilitar con un gesto del usuario (enableAudio()).
// v2.19.5 — se OBSERVA el estado real del AudioContext (running/suspended/interrupted/closed) y se
// permite reanudarlo desde un gesto, además de una prueba aislada de un solo tono con la
// preferencia en OFF. Los patrones y las reglas de disparo NO cambian.

let ctx = null;
let enabled = false;
let tickTimer = null;
const state = { alta: [], media: [] };
const mediaNext = {}; // id -> próximo chime (ms)
let altaLast = 0;
const listeners = new Set();

export function isEnabled() {
  return enabled;
}

function notify() {
  listeners.forEach((f) => { try { f(contextState()); } catch { /* un oyente roto no frena al resto */ } });
}

// Suscripción al estado del contexto: cubre statechange, interrupciones al cambiar de app y
// reanudaciones. Devuelve la función para darse de baja.
export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Estado TÉCNICO del contexto Web Audio. `running` confirma disponibilidad del contexto; no
// demuestra que el dispositivo físico esté emitiendo sonido audible.
export function contextState() {
  return ctx ? ctx.state : null;
}

function ensureContext() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    ctx.addEventListener ? ctx.addEventListener("statechange", notify) : (ctx.onstatechange = notify);
  }
  return ctx;
}

// Tono crudo: no consulta la preferencia (lo usan la prueba aislada y los patrones).
function tone(freq, dur, type = "sine", gainv = 0.08) {
  if (!ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(gainv, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
  o.connect(g);
  g.connect(ctx.destination);
  o.start();
  o.stop(ctx.currentTime + dur);
}

function beep(freq, dur, type = "sine", gainv = 0.08) {
  if (!enabled) return;
  tone(freq, dur, type, gainv);
}

function patronAlta() {
  // Dos tonos agudos, tipo cuadrado: reconocible y urgente.
  beep(1046, 0.14, "square", 0.09);
  setTimeout(() => beep(1318, 0.14, "square", 0.09), 160);
}

function chimeMedia() {
  // Chime suave descendente, tipo seno: distinguible de ALTA.
  beep(660, 0.18, "sine", 0.06);
  setTimeout(() => beep(494, 0.18, "sine", 0.06), 150);
}

function startLoop() {
  if (tickTimer) return;
  tickTimer = setInterval(() => {
    if (!enabled) return;
    const now = Date.now();
    if (state.alta.length && now - altaLast >= 1500) {
      altaLast = now;
      patronAlta();
    }
    for (const m of state.media) {
      if (mediaNext[m.id] == null) mediaNext[m.id] = now; // primer chime inmediato
      if (now >= mediaNext[m.id]) {
        chimeMedia();
        mediaNext[m.id] = now + (m.repiteSeg || 60) * 1000;
      }
    }
  }, 500);
}

function stopLoop() {
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
}

export function enableAudio() {
  try {
    if (!ensureContext()) { enabled = false; notify(); return false; }
    if (ctx.state !== "running") ctx.resume();
    enabled = true;
    startLoop();
    beep(880, 0.12, "sine", 0.05); // confirmación audible de que quedó habilitado
    notify();
    return ctx.state === "running";
  } catch {
    enabled = false;
    notify();
    return false;
  }
}

// Reanudación desde un gesto del usuario: resuelve si el contexto quedó realmente en `running`.
// No reporta éxito falso.
export async function resume() {
  try {
    if (!ensureContext()) return false;
    if (ctx.state !== "running") await ctx.resume();
    notify();
    return ctx.state === "running";
  } catch {
    notify();
    return false;
  }
}

export function disableAudio() {
  enabled = false;
  stopLoop();
  notify();
}

// PRUEBA AISLADA (preferencia en OFF): un único tono corto desde el gesto del usuario. No toca la
// preferencia, no habilita el motor, no arranca el loop y no reactiva alarmas abiertas.
export async function testOnce() {
  try {
    if (!ensureContext()) return false;
    if (ctx.state !== "running") await ctx.resume();
    if (ctx.state !== "running") { notify(); return false; }
    tone(880, 0.2, "sine", 0.07);
    notify();
    return true;
  } catch {
    notify();
    return false;
  }
}

// Muestra de los patrones ALTA/MEDIA para calibrar el volumen del turno (preferencia en ON).
export function test() {
  if (!enabled) enableAudio();
  patronAlta();
  setTimeout(() => chimeMedia(), 1200);
}

// Sincroniza el motor con la lista de alarmas abiertas. Solo suenan las SIN TOMAR ('recibida').
export function sync(alarmas) {
  const alta = [];
  const media = [];
  for (const a of alarmas || []) {
    if (a.estado !== "recibida") continue;
    const p = a.prioridad === "maxima" ? "alta" : a.prioridad;
    if (p === "alta") alta.push({ id: a.id });
    else if (p === "media") media.push({ id: a.id, repiteSeg: (a.sla && a.sla.chime_media_repite_seg) || 60 });
  }
  state.alta = alta;
  state.media = media;
  const ids = new Set(media.map((m) => m.id));
  for (const k of Object.keys(mediaNext)) if (!ids.has(k)) delete mediaNext[k];
}
